import React, { useState } from 'react';
import '../../../../../Assets/styles/LandingStyles/LandingComponentsStyles/Tutorial/TutorialModal.css'
import { ReactComponent as BookSvg } from '../../../../../Assets/svgs/book-open-solid.svg';
import {ReactComponent as GamePadSvg} from '../../../../../Assets/svgs/gamepad-solid.svg';
import {ReactComponent as HandshakeSvg} from '../../../../../Assets/svgs/handshake-angle-solid.svg';
import { ReactComponent as RocketSvg } from '../../../../../Assets/svgs/rocket-solid.svg';

interface TutorialsModalProps {
    onClose: () => void;
}

interface TutorialVideo {
    id: string;
    title: string;
    videoUrl: string;
}

export const TutorialsModal: React.FC<TutorialsModalProps> = ({ onClose }) => {
    const [activeTab, setActiveTab] = useState<'introduction' | 'getting-started' | 'vs-friends' | 'how-to-play'>('introduction');

    // Helper function to extract YouTube video ID from various URL formats
    const extractYouTubeId = (url: string): string => {
        // Handle shorts URLs like https://youtube.com/shorts/4bRXrHooXSA?feature=share
        if (url.includes('youtube.com/shorts/')) {
            const idMatch = url.match(/youtube\.com\/shorts\/([^?&]+)/);
            return idMatch ? idMatch[1] : '';
        }

        // Handle standard YouTube URLs
        const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
        return match ? match[1] : '';
    };

    // Handler to close modal only if the overlay itself is clicked
    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Tutorial videos with YouTube URLs - using the placeholder link
    const tutorialVideos: Record<string, TutorialVideo[]> = {
        'introduction': [
            { id: 'intro-1', title: 'SVO Home', videoUrl: 'https://youtube.com/shorts/1dIetq34DOg?feature=share' } /* DONE */
        ],
        'getting-started': [
            { id: 'gs-1', title: 'Vs. Match Explainer', videoUrl: 'https://youtube.com/shorts/cY9JlIfHUDs' },/* DONE */
            { id: 'gs-2', title: 'How to Create a Vs. Game', videoUrl: 'https://youtube.com/shorts/MKUa9zYPcXA' },/* DONE */
            { id: 'gs-3', title: 'How to Join a Vs. Game', videoUrl: 'https://youtube.com/shorts/FnnkiGUTuGM' },/* DONE */
        ],
        'vs-friends': [
            { id: 'vs-1', title: 'How to Add a Friend', videoUrl: 'https://youtube.com/shorts/BU7468yu6yg' },/* DONE */
            { id: 'vs-2', title: 'How to Challenge a Friend', videoUrl: 'https://youtube.com/shorts/MCPXoP2LjF0' }/* DONE */
        ],
        'how-to-play': [
            { id: 'htp-1', title: 'SVO Single Mode', videoUrl: 'https://youtube.com/shorts/4bRXrHooXSA?feature=share' } /* DONE */
        ]
    };

    // Render tutorial videos for the current tab in a grid layout
    const renderTutorialVideos = () => {
        const videos = tutorialVideos[activeTab] || [];

        return (
            <div className="tutorials-video-grid">
                {videos.map(video => {
                    const videoId = extractYouTubeId(video.videoUrl);

                    return (
                        <div key={video.id} className="tutorials-video-container">
                            <div className="tutorials-video-placeholder">
                                <iframe
                                    src={`https://www.youtube.com/embed/${videoId}`}
                                    title={video.title}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    className="tutorials-video-iframe"
                                />
                                <div className="tutorials-video-title">{video.title}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="tutorials-modal-overlay" onClick={handleOverlayClick}>
            <div className="tutorials-modal-container tutorials-modal">
                {/* Header container */}
                <div className="tutorials-modal-header">
                    <div className="tutorials-modal-title">
                        Tutorials
                        <button className="tutorials-close-button" onClick={onClose}>
                            &#x00D7;
                        </button>
                    </div>
                </div>

                <div className="tutorials-content">
                    {/* Sidebar navigation */}
                    <div className="tutorials-sidebar">
                        <button
                            className={`tutorials-sidebar-button ${activeTab === 'introduction' ? 'active' : ''}`}
                            onClick={() => setActiveTab('introduction')}>
                            <BookSvg className="button-icon" />
                            <span className="button-text">Introduction</span>
                        </button>
                        <button
                            className={`tutorials-sidebar-button ${activeTab === 'getting-started' ? 'active' : ''}`}
                            onClick={() => setActiveTab('getting-started')}>
                            <RocketSvg className="button-icon" />
                            <span className="button-text">Getting Started</span>
                        </button>
                        <button
                            className={`tutorials-sidebar-button ${activeTab === 'vs-friends' ? 'active' : ''}`}
                            onClick={() => setActiveTab('vs-friends')}>
                            <HandshakeSvg className="button-icon" />
                            <span className="button-text">Vs. Friends</span>
                        </button>
                        <button
                            className={`tutorials-sidebar-button ${activeTab === 'how-to-play' ? 'active' : ''}`}
                            onClick={() => setActiveTab('how-to-play')}>
                            <GamePadSvg className="button-icon" />
                            <span className="button-text">How to Play</span>
                        </button>
                    </div>

                    {/* Main content area for videos */}
                    <div className="tutorials-video-content">
                        {renderTutorialVideos()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TutorialsModal;